import React from 'react';
import { fetchAdminCoverage, AdminCoverageResponse } from './password-reset-api';
import { getLocalizedString } from './i18n-utils';

const ADMIN_COVERAGE_STORAGE_KEY = 'adminCoverageData';
const ADMIN_COVERAGE_USERNAME_KEY = 'adminCoverageUsername';

/**
 * Fires an admin-coverage fetch for the given username and stores the result
 * in sessionStorage. Safe to call multiple times – subsequent calls return
 * immediately if data is already cached for this username.
 *
 * Cache is scoped to username - if a different user logs in, the cache is
 * invalidated and refetched.
 *
 * On fetch failure, the cache is populated with the default response so the UI
 * always has a value to display.
 */
export async function prefetchAdminCoverage(username: string): Promise<void> {
  const cachedUsername = sessionStorage.getItem(ADMIN_COVERAGE_USERNAME_KEY);

  // If cached data exists for this username, return early
  if (cachedUsername === username && getCachedAdminCoverage()) {
    return;
  }

  // Different user or no cache - fetch new data
  try {
    const result = await fetchAdminCoverage(username);
    sessionStorage.setItem(ADMIN_COVERAGE_STORAGE_KEY, JSON.stringify(result));
    sessionStorage.setItem(ADMIN_COVERAGE_USERNAME_KEY, username);
  }
  catch {
    const fallback = { hasAdmin: true, contextSupportMessages: [] };
    sessionStorage.setItem(ADMIN_COVERAGE_STORAGE_KEY, JSON.stringify(fallback));
    sessionStorage.setItem(ADMIN_COVERAGE_USERNAME_KEY, username);
  }
}

export function getCachedAdminCoverage(): AdminCoverageResponse | undefined {
  try {
    const cached = sessionStorage.getItem(ADMIN_COVERAGE_STORAGE_KEY);
    return cached ? JSON.parse(cached) : undefined;
  }
  catch {
    return undefined;
  }
}

/** Clears the sessionStorage cache. Only intended for use in unit tests. */
export function resetAdminCoverageCache(): void {
  sessionStorage.removeItem(ADMIN_COVERAGE_STORAGE_KEY);
  sessionStorage.removeItem(ADMIN_COVERAGE_USERNAME_KEY);
}

/**
 * Returns the dynamic "admin support" phrase for the current user.
 * Reads synchronously from the sessionStorage – callers must have
 * awaited `prefetchAdminCoverage` before calling this.
 *
 * Cases:
 *  • No admin:              "please call athenahealth's CSC at (800) 981-5086"
 *  • Admin + instructions:  "refer to your practice's support instructions below:\n[msgs]"
 *  • Default:               "please contact your administrator."
 */
export function getAdminSupportPhrase(): React.ReactNode {
  const coverage = getCachedAdminCoverage();

  if (!coverage) {
    return getLocalizedString('admin.coverage.default');
  }

  if (!coverage.hasAdmin) {
    return getLocalizedString('admin.coverage.no.admin');
  }

  if (coverage.contextSupportMessages.length > 0) {
    return (
      <>
        {getLocalizedString('admin.coverage.support.instructions.prefix')}
        <br />
        <table className="admin-coverage-table">
          <tbody>
            {coverage.contextSupportMessages.map((msg, idx) => (
              <tr key={idx}>
                <td className="admin-coverage-table__context-id">
                  {msg.contextId}
                </td>
                <td className="admin-coverage-table__support-message">
                  {msg.supportMessage}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  return getLocalizedString('admin.coverage.default');
}

/**
 * All variations of admin contact phrases found in error messages.
 * Used by splitAdminContactMessage to detect and split error messages.
 */
export const ADMIN_CONTACT_PHRASES = [
  'Please contact your system administrator to regain access.',
  'Please contact your system administrator to confirm your identity provider.',
  'Please contact your system administrator to confirm the appropriate identity provider for logging into athenaOne.',
  'Please contact your system administrator for assistance.',
  'Please contact your system administrator to update the single sign-on link.',
  'Please contact your system administrator for help.',
  'Please contact your practice administrator.',
  'Please contact your administrator if you need help setting up or managing authentication methods.',
  'Please contact your administrator.',
  'please contact your practice administrator.',
  'please contact your administrator.',
];

/**
 * Splits an error message that ends with an admin contact phrase.
 * Returns the prefix (error context) and whether an admin phrase was found.
 * Used by ErrorContainer to preserve error context while replacing admin contact part.
 */
export function splitAdminContactMessage(message: string): { prefix: string; hasAdminPhrase: boolean } {
  if (!message) {
    return { prefix: '', hasAdminPhrase: false };
  }

  for (const phrase of ADMIN_CONTACT_PHRASES) {
    if (message.endsWith(phrase)) {
      return {
        prefix: message.slice(0, message.length - phrase.length).trimEnd(),
        hasAdminPhrase: true,
      };
    }
  }
  return { prefix: message, hasAdminPhrase: false };
}
